import {ModerationUser, ModerationUserTag} from "interfaces";
import {CSSProperties} from "react";

export enum UserReviewTags {
    Unknown = 'Unknown',
    ScamMessages = 'ScamMessages',
    TooManyLikes = 'TooManyLikes',
    SensitivePhoto = 'SensitivePhoto',
    SimilarReports = 'SimilarReports',
    HiddenAbout = 'HiddenAbout',
    UpdatedPhotos = 'UpdatedPhotos',
    UpdatedAbout = 'UpdatedAbout',
    BannedAbout = 'BannedAbout',
    PremiumScammers = 'PremiumScammers',
    RepeatedMessages = 'RepeatedMessages',
    OnePersonInMultipleVerifications = 'OnePersonInMultipleVerifications',
    ReviewRequiredAbout = 'ReviewRequiredAbout',
    BannedByMultiLogin = 'BannedByMultiLogin',
    IpAddressesReuseWithBanned = 'IpAddressesReuseWithBanned',
    IpAddressesReuseWithSuspicious = 'IpAddressesReuseWithSuspicious',
    IpAddressesReuse = 'IpAddressesReuse',
    SuspiciousName = 'SuspiciousName',
    BannedBySocialLink = 'BannedBySocialLink',
    HiddenName = 'HiddenName',
    BannedName = 'BannedName',
    ReviewRequiredName = 'ReviewRequiredName',
    CelebrityPhoto = 'CelebrityPhoto',
    NoFace = 'NoFace',
    DuplicateFacePhotos = 'DuplicateFacePhotos',
    NoFacesAfterPhotoDeleted = 'NoFacesAfterPhotoDeleted',
    DifferentPeopleInVerification = 'DifferentPeopleInVerification',
    MinorDetected = 'MinorDetected',
    QrCodeInPhoto = 'QrCodeInPhoto',
    BannedPhoto = 'BannedPhoto',
    ReviewRequiredPhoto = 'ReviewRequiredPhoto',
    ReviewRequiredChatMessage = 'ReviewRequiredChatMessage',
    IpAddressReuseAndPhotoDuplicates = 'IpAddressReuseAndPhotoDuplicates',
    IpAddressReuseAndSuspiciousTag = 'IpAddressReuseAndSuspiciousTag',
    IpAddressReuseAndAdminCheckAboutOrPhoto = 'IpAddressReuseAndAdminCheckAboutOrPhoto',
    BannedByDeviceRegionCode = 'BannedByDeviceRegionCode',
    BannedByOldAppVersion = 'BannedByOldAppVersion',
    DuplicateHashPhoto = 'DuplicateHashPhoto',
    SuspiciousDevice = 'SuspiciousDevice',
    SuspiciousOldDevice = 'SuspiciousOldDevice',
    SuspiciousAppCheckDevice = 'SuspiciousAppCheckDevice',
    GreetingAbuse = 'GreetingAbuse',
    NeedsVerificationPhoto = 'NeedsVerificationPhoto',
    NeedsVerificationName = 'NeedsVerificationName',
    NeedsVerificationAbout = 'NeedsVerificationAbout',
    NeedsVerificationChatMessage = 'NeedsVerificationChatMessage',
    SuspiciousPhoto = 'SuspiciousPhoto',
    SuspiciousAbout = 'SuspiciousAbout',
    SuspiciousChatMessage = 'SuspiciousChatMessage',
    BannedByEmptyPhotos = 'BannedByEmptyPhotos',
    EmptyPhoto = 'EmptyPhoto',
    BlackListHash = 'BlackListHash',
    BlackListFace = 'BlackListFace',
    SimilarBlackListHash = 'SimilarBlackListHash',
    ShadowBannedAbout = 'ShadowBannedAbout',
    ShadowBannedName = 'ShadowBannedName',
    ShadowBannedPhoto = 'ShadowBannedPhoto',
    ExplicitSensitivePhoto = 'ExplicitSensitivePhoto',
    SafeSensitivePhoto = 'SafeSensitivePhoto',
    ManualSensitivePhoto = 'ManualSensitivePhoto',
    Registered = 'Registered',
    Deleted = 'Deleted',
    Postponed = 'Postponed',
    AutoBanned = 'AutoBanned',
    BannedByExplicitPhotos = 'BannedByExplicitPhotos',
    SimilarBannedHash = 'SimilarBannedHash',
    ChatSensitivePhoto = 'ChatSensitivePhoto',
}

export enum ReviewTagInfoTypes {
    ChatUser = 'ChatUser',
    Face = 'Face',
    BlackList = 'BlackList',
    Photo = 'Photo',
    User = 'User',
    BannedUser = 'BannedUser'
}

export enum ReviewTagInfoState {
    UserNotExist = 'UserNotExist',
    ChatNotExist = 'ChatNotExist',
    PhotoNotExist = 'PhotoNotExist',
    FaceNotExist = 'FaceNotExist'
}

enum UserReviewTagClass {
    dangerPlus,
    danger,
    suspect,
    info,
    safe,
    manual
}

export const getStyleForUserReviewTag = (tag: UserReviewTags) => {
    const reviewClass = ReviewClassPerTag[tag];

    const StyleClassPerUserReviewTagClass: Record<UserReviewTagClass, {
        bg?: string,
        text?: string,
        style?: CSSProperties,
        highlight?: string
    }> = {
        [UserReviewTagClass.dangerPlus]: {
            text: 'text-white',
            style: {backgroundColor: '#d94c4c'},
        },
        [UserReviewTagClass.danger]: {text: 'text-white', style: {backgroundColor: '#cd5c5c'}},
        [UserReviewTagClass.suspect]: {text: 'text-white', style: {backgroundColor: '#F27820'}},
        [UserReviewTagClass.info]: {text: 'text-black', style: {backgroundColor: '#E3E5E5'}},
        [UserReviewTagClass.safe]: {text: 'text-white', style: {backgroundColor: '#098352'}},
        [UserReviewTagClass.manual]: {text: 'text-black', style: {backgroundColor: '#b0c4de'}},
    }

    const style = StyleClassPerUserReviewTagClass[reviewClass]

    if (!style) {
        console.error(`No style for tag ${tag}`);
    }

    return style ?? StyleClassPerUserReviewTagClass[UserReviewTagClass.suspect];
}

const ReviewClassPerTag: Record<UserReviewTags, UserReviewTagClass> = {
    [UserReviewTags.MinorDetected]: UserReviewTagClass.dangerPlus,
    [UserReviewTags.DuplicateHashPhoto]: UserReviewTagClass.dangerPlus,

    // DANGER – confirmed bans, explicit or illegal content, underage, blacklisted
    [UserReviewTags.BannedAbout]: UserReviewTagClass.danger,
    [UserReviewTags.BannedPhoto]: UserReviewTagClass.danger,
    [UserReviewTags.BannedName]: UserReviewTagClass.danger,
    [UserReviewTags.BannedByMultiLogin]: UserReviewTagClass.danger,
    [UserReviewTags.BannedBySocialLink]: UserReviewTagClass.danger,
    [UserReviewTags.BannedByEmptyPhotos]: UserReviewTagClass.danger,
    [UserReviewTags.BannedByDeviceRegionCode]: UserReviewTagClass.danger,
    [UserReviewTags.BannedByOldAppVersion]: UserReviewTagClass.danger,
    [UserReviewTags.BlackListHash]: UserReviewTagClass.danger,
    [UserReviewTags.BlackListFace]: UserReviewTagClass.danger,
    [UserReviewTags.SimilarBlackListHash]: UserReviewTagClass.danger,
    [UserReviewTags.ExplicitSensitivePhoto]: UserReviewTagClass.danger,
    [UserReviewTags.AutoBanned]: UserReviewTagClass.danger,
    [UserReviewTags.BannedByExplicitPhotos]: UserReviewTagClass.danger,
    [UserReviewTags.PremiumScammers]: UserReviewTagClass.danger,
    [UserReviewTags.OnePersonInMultipleVerifications]: UserReviewTagClass.danger,
    [UserReviewTags.IpAddressesReuseWithBanned]: UserReviewTagClass.danger,
    [UserReviewTags.IpAddressesReuseWithSuspicious]: UserReviewTagClass.danger,
    [UserReviewTags.IpAddressReuseAndPhotoDuplicates]: UserReviewTagClass.danger,
    [UserReviewTags.IpAddressReuseAndSuspiciousTag]: UserReviewTagClass.danger,
    [UserReviewTags.IpAddressReuseAndAdminCheckAboutOrPhoto]: UserReviewTagClass.danger,
    [UserReviewTags.ShadowBannedAbout]: UserReviewTagClass.danger,
    [UserReviewTags.ShadowBannedName]: UserReviewTagClass.danger,
    [UserReviewTags.ShadowBannedPhoto]: UserReviewTagClass.danger,
    [UserReviewTags.DuplicateFacePhotos]: UserReviewTagClass.danger,
    [UserReviewTags.SimilarBannedHash]: UserReviewTagClass.danger,

    // SUSPECT – patterns, possible fraud/spam, or needs manual check
    [UserReviewTags.ScamMessages]: UserReviewTagClass.suspect,
    [UserReviewTags.TooManyLikes]: UserReviewTagClass.suspect,
    [UserReviewTags.SensitivePhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.ChatSensitivePhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.RepeatedMessages]: UserReviewTagClass.suspect,
    [UserReviewTags.IpAddressesReuse]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousName]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousDevice]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousOldDevice]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousAppCheckDevice]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousPhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousAbout]: UserReviewTagClass.suspect,
    [UserReviewTags.SuspiciousChatMessage]: UserReviewTagClass.suspect,
    [UserReviewTags.SimilarReports]: UserReviewTagClass.suspect,
    [UserReviewTags.CelebrityPhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.QrCodeInPhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.DifferentPeopleInVerification]: UserReviewTagClass.suspect,
    [UserReviewTags.NeedsVerificationPhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.NeedsVerificationName]: UserReviewTagClass.suspect,
    [UserReviewTags.NeedsVerificationAbout]: UserReviewTagClass.suspect,
    [UserReviewTags.NeedsVerificationChatMessage]: UserReviewTagClass.suspect,
    [UserReviewTags.ReviewRequiredAbout]: UserReviewTagClass.suspect,
    [UserReviewTags.ReviewRequiredName]: UserReviewTagClass.suspect,
    [UserReviewTags.ReviewRequiredPhoto]: UserReviewTagClass.suspect,
    [UserReviewTags.ReviewRequiredChatMessage]: UserReviewTagClass.suspect,
    [UserReviewTags.GreetingAbuse]: UserReviewTagClass.suspect,

    // INFO – benign, updates, neutral or system state
    [UserReviewTags.Unknown]: UserReviewTagClass.info,
    [UserReviewTags.UpdatedPhotos]: UserReviewTagClass.info,
    [UserReviewTags.UpdatedAbout]: UserReviewTagClass.info,
    [UserReviewTags.EmptyPhoto]: UserReviewTagClass.info,
    [UserReviewTags.Registered]: UserReviewTagClass.info,
    [UserReviewTags.Deleted]: UserReviewTagClass.info,
    [UserReviewTags.Postponed]: UserReviewTagClass.info,
    [UserReviewTags.HiddenAbout]: UserReviewTagClass.info,
    [UserReviewTags.HiddenName]: UserReviewTagClass.info,
    [UserReviewTags.SafeSensitivePhoto]: UserReviewTagClass.safe,
    [UserReviewTags.ManualSensitivePhoto]: UserReviewTagClass.manual,
    [UserReviewTags.NoFace]: UserReviewTagClass.info,
    [UserReviewTags.NoFacesAfterPhotoDeleted]: UserReviewTagClass.info,
}

export const isReviewTagPresent = (state?: ReviewTagInfoState) => {
    return !state || ![ReviewTagInfoState.ChatNotExist, ReviewTagInfoState.PhotoNotExist, ReviewTagInfoState.FaceNotExist, ReviewTagInfoState.UserNotExist].includes(state);
}


export const SENSITIVE_TAGS = [
    UserReviewTags.SensitivePhoto,
    UserReviewTags.SafeSensitivePhoto,
    UserReviewTags.ExplicitSensitivePhoto,
    UserReviewTags.ManualSensitivePhoto,
    UserReviewTags.MinorDetected,
]

export const PHOTO_TAGS = [
    ...SENSITIVE_TAGS,
    UserReviewTags.SuspiciousPhoto,
    UserReviewTags.QrCodeInPhoto,
    UserReviewTags.CelebrityPhoto,
    UserReviewTags.BannedPhoto,
    UserReviewTags.ReviewRequiredPhoto,
    UserReviewTags.ShadowBannedPhoto,
    UserReviewTags.DuplicateFacePhotos,
    UserReviewTags.DuplicateHashPhoto,
    UserReviewTags.NoFace,
]


export const getPhotoInfosFromTag = (user: ModerationUser, mtag: ModerationUserTag):
    Array<{ key: string, info: string }> | null => {
    const {tag, infoType, infoValue, info, relatedInfos} = mtag;
    if (tag === UserReviewTags.DuplicateFacePhotos) {
        if (infoValue) {
            const duplicateFace = user.duplicateFaces?.find(v => v.faceId === infoValue);
            if (duplicateFace) {
                return [{key: duplicateFace.photoKey, info: ''}]
            }
        } else {
            return relatedInfos?.filter(i => i.infoType === ReviewTagInfoTypes.Photo)?.map(i => ({
                key: i.infoValue ?? '',
                info: ''
            })) ?? [];
        }
    }

    if (infoType === ReviewTagInfoTypes.Face) {
        return null
    }

    if (infoType === ReviewTagInfoTypes.Photo) {
        return infoValue ? [{key: infoValue, info: ''}] : null
    }

    if (tag === UserReviewTags.UpdatedPhotos) {
        if (relatedInfos?.length) {
            return relatedInfos?.filter(i => i.infoType === ReviewTagInfoTypes.Photo)?.map(i => ({
                key: i.infoValue ?? '',
                info: ''
            })) ?? [];
        } else {
            const keys = info ? info.includes('\n') ? info.split('\n') : [info] : null
            return keys?.map(k => ({key: k, info: ''})) ?? null
        }
    }


    if (PHOTO_TAGS.includes(tag)) {
        const parts = info ? info.split('\n') : []
        return [{key: parts[0], info: parts[1]}];
    }

    return null
}

export const getPhotoTags = (user: ModerationUser, photoKey?: string) => {
    const tags = user.reviewTags;
    return tags.filter(t => {
            const photoInfos = getPhotoInfosFromTag(user, t);
            const photoKeys = photoInfos ? photoInfos.map(v => v.key) : null
            const hasPhotoKey = (photoKey && photoKeys ? photoKeys.includes(photoKey) : false);
            return (t.infoType === ReviewTagInfoTypes.Photo && (photoKey ? t.infoValue === photoKey : true))
                || (PHOTO_TAGS.includes(t.tag) && hasPhotoKey)
        }
    )
}

